import { match } from 'ts-pattern';

import { cn } from '@nesso/ui/lib/utils';
import { Alert, AlertAction, AlertDescription, AlertTitle } from '@nesso/ui/primitives/alert';
import { Button } from '@nesso/ui/primitives/button';

import { formatMonthDay, formatRelativeDays } from '@/shared/lib/time-utils';

type SubscriptionStatusAlertProps = {
    onAction?: () => void;
    // 'container' switches on the alert's own width; 'viewport' on the `sm` breakpoint.
    responsive?: 'container' | 'viewport';
} & (
    | { variant: 'expired'; retentionEndsAt?: string | null }
    | { variant: 'cancelling'; endsAt?: string | null; isReactivating?: boolean }
);

const INLINE_ACTION = {
    container:
        '@min-[480px]:col-start-3 @min-[480px]:row-start-1 @min-[480px]:row-span-2 @min-[480px]:mt-0 @min-[480px]:ml-2 @min-[480px]:self-center @min-[480px]:justify-self-auto',
    viewport:
        'sm:col-start-3 sm:row-start-1 sm:row-span-2 sm:mt-0 sm:ml-2 sm:self-center sm:justify-self-auto',
} as const;
const BUTTON_SIZE = {
    container: '@min-[480px]:h-6 @min-[480px]:px-3',
    viewport: 'sm:h-6 sm:px-3',
} as const;

export function SubscriptionStatusAlert(props: SubscriptionStatusAlertProps) {
    const { onAction, responsive = 'container' } = props;

    const { title, description, actionLabel } = match(props)
        .with({ variant: 'expired' }, ({ retentionEndsAt }) => ({
            title: 'Your plan expired',
            description: `Your account will be deleted${
                retentionEndsAt ? ` ${formatRelativeDays(retentionEndsAt)}` : ''
            }. Choose a plan to continue using Nesso.`,
            actionLabel: 'Choose Plan',
        }))
        .with({ variant: 'cancelling' }, ({ endsAt, isReactivating }) => ({
            title: 'You canceled your plan',
            description: `Reactivate your plan to continue using Nesso${
                endsAt ? ` after ${formatMonthDay(endsAt)}` : ''
            }`,
            actionLabel: isReactivating ? 'Reactivating' : 'Reactivate Plan',
        }))
        .exhaustive();

    const isActionDisabled = props.variant === 'cancelling' && !!props.isReactivating;

    return (
        <Alert variant="warning" className="grid-cols-[0_minmax(0,1fr)_auto] gap-x-0 mb-0">
            <AlertTitle className="text-sm">{title}</AlertTitle>
            <AlertDescription className="text-sm">{description}</AlertDescription>
            {onAction && (
                <AlertAction className={INLINE_ACTION[responsive]}>
                    <Button
                        className={cn('h-9 px-4 text-sm', BUTTON_SIZE[responsive])}
                        variant="outline"
                        size="sm"
                        onClick={onAction}
                        disabled={isActionDisabled}
                    >
                        {actionLabel}
                    </Button>
                </AlertAction>
            )}
        </Alert>
    );
}
